import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, X, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast.store';

import { ConsentBanner } from './components/ConsentBanner';
import { RelationshipBadge } from './components/RelationshipBadge';
import { HorizontalSubScreeningStepper } from './HorizontalSubScreeningStepper';
import { DomainForm } from './DomainForm';
import {
  DOMAIN_ORDER,
  defaultAnswerFor,
  isDomainValid,
  type DomainAnswer,
} from './domains';
import type { HrsnDomain } from '@/types/api';
import {
  useDebouncedSaveAnswer,
  useTimeEventLogger,
  useSubmitScreening,
} from './useScreening';
import { householdApi } from './household.api';
import type { HouseholdDerivedState } from './derive-household-state';

/** Border color per derived state — visually echoes the row badge so the
 *  panel reads as the "expanded view" of that member's lifecycle stage. */
const PANEL_BORDER_CLASS: Record<HouseholdDerivedState, string> = {
  pending: 'border-nav-line',
  in_progress: 'border-nav-amber',
  completed: 'border-nav-teal',
  validated: 'border-nav-sage',
  declined: 'border-nav-line',
  rejected_by_uploader: 'border-nav-red',
};

/**
 * Cluster H v2 — inline expandable wizard, mounted under a household member
 * row by HouseholdMembersStep. Replaces SubScreeningDrawer (LD-H2-02: no
 * drawer overlay; inline acordeon only).
 *
 * Lifecycle (state machine):
 *   init → (existingSubScreeningId ? skip : promoting) → consent_pending →
 *     wizard_active → (submit_confirm → submitting → submitted)
 *
 * No abandon flow. Closing the panel (X button or onClose) is non-destructive
 * — the sub-screening row persists and the member's derivedState stays
 * 'in_progress' until either the agent submits OR the reaper auto-cancels.
 * Terminal "no" is handled OUTSIDE the panel via the Decline button +
 * decline endpoint (LD-H2-08).
 */

type PanelStatus =
  | 'init'
  | 'promoting'
  | 'consent_pending'
  | 'wizard_active'
  | 'submit_confirm'
  | 'submitting'
  | 'submitted';

interface HouseholdScreeningPanelProps {
  parentScreeningId: string;
  hmId: string;
  fullName: string;
  dobYear: number;
  /** Sub-screening id from a prior open, if any. When non-null the promote
   *  step is skipped and the panel resumes at consent_pending → wizard_active. */
  existingSubScreeningId: string | null;
  /** Collapses the panel (non-destructive). The sub-screening row persists. */
  onClose: () => void;
  /** Called after a successful submit so the parent can invalidate the
   *  household-states query and re-render the row badge as 'completed'. */
  onSubmitted: () => void;
  /** Optional relationship label (parsed by parent from member notes). */
  relationship?: string | null;
  /** Derived state from deriveHouseholdScreeningState(); drives the panel's
   *  colored left border so the panel echoes the row badge. Defaults to
   *  'in_progress' (amber) if unknown — the panel is normally only mounted
   *  for non-terminal states (pending / in_progress / completed). */
  derivedState?: HouseholdDerivedState;
}

export function HouseholdScreeningPanel({
  parentScreeningId,
  hmId,
  fullName,
  dobYear,
  existingSubScreeningId,
  onClose,
  onSubmitted,
  relationship,
  derivedState = 'in_progress',
}: HouseholdScreeningPanelProps): JSX.Element {
  const t = useT();
  const qc = useQueryClient();

  const [status, setStatus] = useState<PanelStatus>('init');
  const [subScreeningId, setSubScreeningId] = useState<string | null>(existingSubScreeningId);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Minor detection — proxy consent required when subject is under 18.
  const isMinor = useMemo(() => {
    if (!dobYear) return false;
    return new Date().getFullYear() - dobYear < 18;
  }, [dobYear]);

  // Promote mutation. Only fires when no existing sub-screening was provided.
  const promoteMut = useMutation({
    mutationFn: () => householdApi.promoteAndScreen(parentScreeningId, hmId, {}),
    onSuccess: (r) => {
      setSubScreeningId(r.screeningId);
      setStatus('consent_pending');
      // Refresh the parent's household-states query so the member badge
      // updates immediately to 'in_progress' even before the agent
      // touches a single domain.
      void qc.invalidateQueries({
        queryKey: ['screening', parentScreeningId, 'household-states'],
      });
    },
    onError: (err) => {
      // FU-1 / A3. NestJS wraps a thrown object payload as
      // {statusCode, message: {code, message, ...}}, and api-client's
      // extractMessage only unwraps `message` when it is a string or an array
      // -- so every typed 409 this endpoint sends arrived here as "Conflict".
      // ApiError keeps the payload; reading it costs three lines and turns
      // "Conflict" into the sentence the server actually wrote.
      const payload = (err as { payload?: { message?: unknown } })?.payload;
      const inner = payload?.message;
      const structured =
        inner && typeof inner === 'object' && !Array.isArray(inner)
          ? (inner as { code?: string; message?: string })
          : null;
      const msg = structured?.message || (err as Error).message || String(err);
      setLastError(structured?.code ? `${structured.code}: ${msg}` : msg);
      setStatus('init');
      toast.error(t('subScreening.errors.openFailed'), msg);
    },
  });

  // Fire promote (or skip to consent) once on first mount.
  const initFiredRef = useRef(false);
  useEffect(() => {
    if (initFiredRef.current) return;
    if (status !== 'init') return;
    initFiredRef.current = true;
    if (existingSubScreeningId) {
      setStatus('consent_pending');
    } else {
      setStatus('promoting');
      promoteMut.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC closes the panel (collapse, non-destructive).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Submit mutation lifted so the inline confirm panel can invoke it.
  // Called unconditionally per React hook-order rules; '' fallback is safe
  // because mutate() only fires from the submit_confirm action.
  const submitMut = useSubmitScreening(subScreeningId ?? '');

  // ----- Header -----
  const header = (
    <div className="flex items-center justify-between gap-2 border-b border-nav-card pb-3">
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-nav-tealDark">
          {t('householdScreening.header.title')}
        </span>
        <span className="text-base font-medium text-nav-navy truncate">{fullName}</span>
        {relationship ? <RelationshipBadge relationship={relationship} /> : null}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );

  // ----- Body -----
  let body: JSX.Element;
  if (status === 'init' || status === 'promoting') {
    body = (
      <div className="flex flex-col items-center justify-center min-h-[160px] gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-nav-teal" aria-hidden />
        <p className="text-sm text-nav-navy">{t('subScreening.status.promoting')}</p>
      </div>
    );
  } else if (status === 'consent_pending' && !consentConfirmed) {
    body = (
      <ConsentBanner
        type={isMinor ? 'proxy' : 'verbal'}
        subjectName={fullName}
        onConfirm={() => {
          setConsentConfirmed(true);
          setStatus('wizard_active');
        }}
      />
    );
  } else if (status === 'submitting') {
    body = (
      <div className="flex flex-col items-center justify-center min-h-[160px] gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-nav-teal" aria-hidden />
        <p className="text-sm text-nav-navy">{t('subScreening.status.submitting')}</p>
      </div>
    );
  } else if (subScreeningId) {
    body = (
      <PanelWizard
        subScreeningId={subScreeningId}
        showSubmitConfirm={status === 'submit_confirm'}
        onSubmitClick={() => setStatus('submit_confirm')}
        onCancelSubmit={() => setStatus('wizard_active')}
        onConfirmSubmit={() => {
          if (!subScreeningId) return;
          setStatus('submitting');
          submitMut
            .mutateAsync({
              timeCategory: 'under_8',
              nextStepNavigator: false,
              findhelpSubmitted: false,
              findhelpScreeningId: undefined,
            })
            .then(() => {
              toast.success(t('subScreening.submit.success'));
              setStatus('submitted');
              void qc.invalidateQueries({
                queryKey: ['screening', parentScreeningId, 'household-states'],
              });
              onSubmitted();
            })
            .catch((err: unknown) => {
              const msg = (err as Error)?.message ?? String(err);
              setLastError(msg);
              setStatus('wizard_active');
              toast.error(t('subScreening.errors.submitFailed'), msg);
            });
        }}
      />
    );
  } else {
    body = (
      <Alert variant="warning" role="alert">
        <AlertTitle>{t('subScreening.errors.openFailed')}</AlertTitle>
        <AlertDescription>(status: {status})</AlertDescription>
      </Alert>
    );
  }

  return (
    <div
      className={cn(
        'mt-3 rounded-md border border-nav-card bg-nav-softer p-4 shadow-sm flex flex-col gap-4',
        'border-l-4',
        PANEL_BORDER_CLASS[derivedState],
      )}
    >
      {header}
      {lastError && status !== 'init' && status !== 'promoting' ? (
        <div className="flex items-center gap-1.5 text-xs text-danger-700">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden />
          <span>{lastError}</span>
        </div>
      ) : null}
      {body}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard — compact single-column layout for the inline panel.
// ---------------------------------------------------------------------------

interface PanelWizardProps {
  subScreeningId: string;
  showSubmitConfirm: boolean;
  onSubmitClick: () => void;
  onCancelSubmit: () => void;
  onConfirmSubmit: () => void;
}

function PanelWizard({
  subScreeningId,
  showSubmitConfirm,
  onSubmitClick,
  onCancelSubmit,
  onConfirmSubmit,
}: PanelWizardProps): JSX.Element {
  const t = useT();
  const [currentDomain, setCurrentDomain] = useState<HrsnDomain>(DOMAIN_ORDER[0]!);
  const { schedule, isSaving } = useDebouncedSaveAnswer(subScreeningId);
  const logEvent = useTimeEventLogger(subScreeningId);

  const [answers, setAnswers] = useState<Record<HrsnDomain, DomainAnswer>>(() => {
    const init: Partial<Record<HrsnDomain, DomainAnswer>> = {};
    for (const d of DOMAIN_ORDER) init[d] = defaultAnswerFor(d);
    return init as Record<HrsnDomain, DomainAnswer>;
  });
  const [notesByDomain, setNotesByDomain] = useState<Record<HrsnDomain, string>>(
    () => Object.fromEntries(DOMAIN_ORDER.map((d) => [d, ''])) as Record<HrsnDomain, string>,
  );

  // Log call_started once when the wizard first becomes visible.
  const startLoggedRef = useRef(false);
  useEffect(() => {
    if (startLoggedRef.current) return;
    startLoggedRef.current = true;
    void logEvent('call_started');
  }, [logEvent]);

  const validDomains = useMemo<ReadonlySet<HrsnDomain>>(
    () => new Set(DOMAIN_ORDER.filter((d) => isDomainValid(d, answers[d]!))),
    [answers],
  );
  const positiveDomains = useMemo<ReadonlySet<HrsnDomain>>(() => new Set(), []);
  const allAnswered = validDomains.size === DOMAIN_ORDER.length;

  const handleAnswerChange = (next: DomainAnswer): void => {
    setAnswers((prev) => ({ ...prev, [currentDomain]: next }));
    schedule({
      domain: currentDomain,
      answers: next as Record<string, unknown>,
      notes: notesByDomain[currentDomain] ?? '',
    });
  };

  const handleNotesChange = (notes: string): void => {
    setNotesByDomain((prev) => ({ ...prev, [currentDomain]: notes }));
    schedule({
      domain: currentDomain,
      answers: answers[currentDomain]! as Record<string, unknown>,
      notes,
    });
  };

  const goPrev = (): void => {
    const idx = DOMAIN_ORDER.indexOf(currentDomain);
    if (idx > 0) setCurrentDomain(DOMAIN_ORDER[idx - 1]!);
  };
  const goNext = (): void => {
    const idx = DOMAIN_ORDER.indexOf(currentDomain);
    if (idx < DOMAIN_ORDER.length - 1) setCurrentDomain(DOMAIN_ORDER[idx + 1]!);
  };
  const onLastDomain = currentDomain === DOMAIN_ORDER[DOMAIN_ORDER.length - 1];

  if (showSubmitConfirm) {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-nav-navy">
          {t('subScreening.submit.confirmTitle')}
        </p>
        <p className="text-xs text-nav-navy">
          {t('subScreening.submit.confirmBody', { count: DOMAIN_ORDER.length, name: '' })}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelSubmit}>
            {t('subScreening.submit.cancelButton')}
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirmSubmit}>
            {t('subScreening.submit.submitButton')}
          </Button>
        </div>
      </div>
    );
  }

  const currentIdx = DOMAIN_ORDER.indexOf(currentDomain);

  return (
    <div className="space-y-4">
      {/* Cluster N v0.6.0 Fase 11.10 — Option C Hybrid horizontal stepper.
          Replaces the vertical Stepper from Cluster H v2. Visual approved
          via Dispatch mockup 2026-05-21. */}
      <HorizontalSubScreeningStepper
        currentDomain={currentDomain}
        validDomains={validDomains}
        positiveDomains={positiveDomains}
        onSelect={setCurrentDomain}
      />
      {/* Per-question header: PREGUNTA X badge + En progreso pill */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-nav-soft text-nav-navy">
          {t('subScreeningStepper.pregunta.label', { n: currentIdx + 1 })}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-nav-tealTint text-nav-tealDark border border-nav-teal">
          {t('subScreeningStepper.pregunta.enProgreso')}
        </span>
      </div>
      <DomainForm
        domain={currentDomain}
        answer={answers[currentDomain]!}
        onChange={handleAnswerChange}
        notes={notesByDomain[currentDomain] ?? ''}
        onNotesChange={handleNotesChange}
      />
      <div className="flex items-center justify-between gap-2 border-t border-nav-card pt-3">
        <span className={cn('text-[11px]', isSaving ? 'text-nav-tealDark' : 'text-nav-slate')}>
          {isSaving ? t('subScreening.save.saving') : t('subScreening.save.saved')}
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={goPrev}
            disabled={currentDomain === DOMAIN_ORDER[0]}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            {t('subScreening.actions.back')}
          </Button>
          {onLastDomain ? (
            <Button
              variant="primary"
              size="sm"
              onClick={onSubmitClick}
              disabled={!allAnswered}
            >
              {t('subScreening.submit.submitButton')}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={goNext}>
              {t('subScreening.actions.next')}
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
